import { create } from "zustand";
import { api } from "../lib/api";

export interface TransformStep {
  id: string;
  equation: string;
  transform: string;
  explanation: string;
}

export interface Parameter {
  name: string;
  min: number;
  max: number;
  step: number;
  value: number;
}

export type TransformType =
  | "simplify"
  | "expand"
  | "factor"
  | "substitute"
  | "differentiate"
  | "integrate"
  | "isolate";

interface EquationPlaygroundState {
  isOpen: boolean;
  equation: string;
  originalEquation: string;
  steps: TransformStep[];
  parameters: Parameter[];
  selectedSubExpr: string | null;
  isTransforming: boolean;
  error: string | null;

  open: (equation: string) => void;
  close: () => void;
  setEquation: (equation: string) => void;
  applyTransform: (transform: TransformType, variable?: string) => Promise<void>;
  analyzeEquation: (equation: string) => Promise<void>;
  updateParameter: (name: string, value: number) => void;
  setSelectedSubExpr: (expr: string | null) => void;
  undoStep: () => void;
  jumpToStep: (index: number) => void;
  getInsertEquation: () => string;
  reset: () => void;
}

let stepCounter = 0;

export const useEquationPlaygroundStore = create<EquationPlaygroundState>()(
  (set, get) => ({
    isOpen: false,
    equation: "",
    originalEquation: "",
    steps: [],
    parameters: [],
    selectedSubExpr: null,
    isTransforming: false,
    error: null,

    open: (equation) => {
      set({
        isOpen: true,
        equation,
        originalEquation: equation,
        steps: [],
        parameters: [],
        selectedSubExpr: null,
        error: null,
      });
      get().analyzeEquation(equation);
    },

    close: () => set({ isOpen: false }),

    setEquation: (equation) => set({ equation, error: null }),

    applyTransform: async (transform, variable) => {
      const { equation } = get();
      if (!equation.trim()) return;

      set({ isTransforming: true, error: null });

      try {
        const resp = await api.post<{
          result: string;
          explanation: string;
          steps: { expression: string; description: string }[];
        }>("/api/v1/ai/equation-transform", {
          equation,
          transform,
          variable,
        });

        const step: TransformStep = {
          id: `step-${++stepCounter}`,
          equation: resp.result,
          transform,
          explanation: resp.explanation,
        };

        set((s) => ({
          equation: resp.result,
          steps: [...s.steps, step],
          isTransforming: false,
        }));
      } catch (err) {
        set({
          isTransforming: false,
          error: err instanceof Error ? err.message : "Transform failed",
        });
      }
    },

    analyzeEquation: async (equation) => {
      if (!equation.trim()) return;

      try {
        const resp = await api.post<{
          variables: { name: string; min: number; max: number }[];
        }>("/api/v1/ai/equation-analyze", { equation });

        const parameters: Parameter[] = resp.variables.map((v) => ({
          name: v.name,
          min: v.min ?? -10,
          max: v.max ?? 10,
          step: 0.1,
          value: 1,
        }));

        set({ parameters });
      } catch {
        // Non-critical: silently ignore analysis failures
      }
    },

    updateParameter: (name, value) =>
      set((s) => ({
        parameters: s.parameters.map((p) =>
          p.name === name ? { ...p, value } : p,
        ),
      })),

    setSelectedSubExpr: (expr) => set({ selectedSubExpr: expr }),

    undoStep: () => {
      const { steps, originalEquation } = get();
      if (steps.length === 0) return;

      const newSteps = steps.slice(0, -1);
      const equation = newSteps.length > 0
        ? newSteps[newSteps.length - 1].equation
        : originalEquation;

      set({ steps: newSteps, equation });
    },

    jumpToStep: (index) => {
      const { steps, originalEquation } = get();
      if (index < 0) {
        set({ equation: originalEquation, steps: [] });
        return;
      }
      if (index >= steps.length) return;

      set({
        equation: steps[index].equation,
        steps: steps.slice(0, index + 1),
      });
    },

    getInsertEquation: () => get().equation,

    reset: () =>
      set({
        isOpen: false,
        equation: "",
        originalEquation: "",
        steps: [],
        parameters: [],
        selectedSubExpr: null,
        isTransforming: false,
        error: null,
      }),
  }),
);
